import React, { useState, useEffect } from 'react';
import {
    Modal, ModalBody, Form, FormGroup, Input, Label, Nav, NavItem, NavLink, TabContent, TabPane, Row, Col,
} from 'reactstrap';
import classnames from 'classnames';
import { OrganizationalRoleType } from '../../constants/OrganizationalRoleType';
import { usePermissionRolesByRoleIdQuery, useSystemPermissionDependencyQuery } from '../../utilities/queries/useRolesQuery';
import { system } from '../../constants/system';
import { securityLevel } from '../../constants/securityLevel';
import { isEqual } from 'lodash';
import { useUpdateRolePermissionsMutation } from '../../utilities/mutations/useUpdateRolePermissionsMutation';
import PermissionsTabContent from '../../components/PermissionsTabContent/PermissionsTabContent';
import { SquareCheckbox } from '../../components/Inputs/SquareCheckbox/SquareCheckbox';
import ActionModalHeader from '../../components/ActionModalHeader/ActionModalHeader';
import Button from '../../components/UIButton/UIButton';
import Spinner from '../../components/UISpinner/UISpinner';


const systemTabs = Object.keys(system);

let permissionLevels = {
    high: [],
    medium: [],
    low: []
};

const  categorizePermissions = (permissionList) => {
    const low = [];
    const medium = [];
    const high = [];

    for (const perm of permissionList) {
        switch (perm.securityLevel) {
            case securityLevel.low:
                low.push(perm);
                break;
            case securityLevel.medium:
                medium.push(perm);
                break;
            case securityLevel.high:
                high.push(perm);
                break;
            default:
                break; // skip unknown levels
        }
    }

    return { low, medium, high };
}

function EditRolePermissionsModal({ isOpen, toggle, data, makeToast }) {

    const { data: rolePermissionsDetail, isLoading, error:rolesError } = usePermissionRolesByRoleIdQuery(data?.roleId);
    const [localRolePermissionsDetail, setLocalRolePermissionsDetail] = useState(null);
    const isAppUserRole = data.roleType === OrganizationalRoleType.AppUser;
    const { isPending: isUpdateLoading, mutateAsync: updateAsync, error: updateError, reset: updateReset } = useUpdateRolePermissionsMutation();

    useEffect(() => {
        if (rolePermissionsDetail) {
            setLocalRolePermissionsDetail(rolePermissionsDetail);
        } else {
            setLocalRolePermissionsDetail({
                lastUpdated: data.lastUpdated,
                organizationalRoleId: data.roleId,
                organizationalRoleType: data.roleType,
                roleName: data.roleName,
                permissionList: []
            });
        }
    }, [rolePermissionsDetail, data]);

    if (localRolePermissionsDetail) {
        const { low, medium, high } = categorizePermissions(localRolePermissionsDetail?.permissionList ?? []);
        permissionLevels.low = low;
        permissionLevels.medium = medium;
        permissionLevels.high = high;
    }
    //Permission Dependency : To track a permission that is dependent on another one
    const mapDependencies = (dependencies) => {
        const childParentDict = {}; // key: dependent/child permissionId, value: parent permissionsId
        const parentChildDict = {}; // key: parent permissionsId, value: dependent/child
        dependencies.forEach(dep => {
            childParentDict[dep.childPermissionId] = dep.parentPermissionId;
            parentChildDict[dep.parentPermissionId] = dep.childPermissionId;
        });
        return {
            dependencyDictionaryChildParent: childParentDict,
            dependencyDictionaryParentChild: parentChildDict
        };
    }

    const isParentChecked = (systemPermissionId) => {
        if (dependencyDictionaryChildParent[systemPermissionId]) {
            const parentSystemPermissionId = dependencyDictionaryChildParent[systemPermissionId];
            const match = localRolePermissionsDetail?.permissionList
                ?.find(perm => perm.systemPermissionId === parentSystemPermissionId);

            const isChecked = match?.value ?? false;
            return isChecked;
        }
        return true;
    }
    const { data: permissionDependencyList, isLoading:isDepListLoading, error: dependencyError } = useSystemPermissionDependencyQuery();
    const {
        dependencyDictionaryChildParent = {},
        dependencyDictionaryParentChild = {}
    } = permissionDependencyList ? mapDependencies(permissionDependencyList) : {};


    const isPermissionsListEmpty = (tabName) => {
        let tempLow = permissionLevels.low.filter((item) => item.systemId === system[tabName]);
        let tempMedium = permissionLevels.medium.filter((item) => item.systemId === system[tabName]);
        let tempHigh = permissionLevels.high.filter((item) => item.systemId === system[tabName]);
        return !tempLow.length && !tempMedium.length && !tempHigh.length;
    }
    const isCustomRole = data.roleType === OrganizationalRoleType.Custom;
    const [activeTab, setActiveTab] = useState(Object.keys(system)[0]);
    const isActiveTabEmpty = isPermissionsListEmpty(activeTab);
    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const toggleModal = () => {
        toggle();
        permissionLevels = {
            high: [],
            medium: [],
            low: []
        };
    }

    const handleCheckboxToggle = (systemPermissionId) => {
        setLocalRolePermissionsDetail((prev) => {
            if (!prev) return prev;

            const childPermissionId = dependencyDictionaryParentChild[systemPermissionId];

            const updatedPermissions = prev.permissionList.map((perm) => {
                // Toggle the parent permission
                if (perm.systemPermissionId === systemPermissionId) {
                    const newValue = !perm.value;

                    return { ...perm, value: newValue };
                }

                // If this is the child, and parent is being unchecked, disable the child
                if (
                    perm.systemPermissionId === childPermissionId &&
                    prev.permissionList.find(p => p.systemPermissionId === systemPermissionId)?.value === true
                ) {
                    return { ...perm, value: false }; // disable child if parent is being unchecked
                }

                return perm;
            });

            return {
                ...prev,
                permissionList: updatedPermissions,
            };
        });
    };


    const handleAllCheckboxesToggle = (checked) => {
        setLocalRolePermissionsDetail((prev) => {
            if (!prev) return prev;
            const updatedPermissions = prev.permissionList.map((perm) =>
                perm.systemId === system[activeTab]
                    ? { ...perm, value: checked }
                    : perm
            );

            return {
                ...prev,
                permissionList: updatedPermissions
            };
        });
    };

    const hadleSaveChanges = async () => {
        if (!isEqual(rolePermissionsDetail, localRolePermissionsDetail)) {
            await updateAsync({ updateData:localRolePermissionsDetail, makeToast });
        } else {
            makeToast("There are no updates to apply.","info");
            return;
        }
        toggleModal();
    }
    const handleCancel = () => {
        updateReset();
        toggleModal();
    }




    const renderPermissionCheckboxes = (level, activeTab) => (
        permissionLevels[level]
            .filter((perm) => perm.systemId === system[activeTab] && isParentChecked(perm.systemPermissionId)) // Only show permissions for the activeTab systemId
            .map((perm, idx) => (
                <SquareCheckbox
                    key={perm.systemPermissionId}
                    id={`perm-${perm.systemPermissionId}`}
                    value={perm.value}
                    valueChangeHandler={() => handleCheckboxToggle(perm.systemPermissionId)}
                    label={perm.permissionName}
                    checkboxClassName="pb-1"
                />
            ))
    );

    const roleNameTextBox = !isCustomRole ? <Input
        id="roleName"
        value={data.roleName ?? ""}
        disabled={true}
        className="form-control form-control-sm"
    /> : <Input
        id="roleName"
            value={localRolePermissionsDetail?.roleName ?? ""}
            className="form-control form-control-sm"
        onChange={(e) => setLocalRolePermissionsDetail((prev) => ({ ...prev, roleName: e.target.value }))}
    />
    const getUIBusyMessage = () => {
        if (isUpdateLoading) return "Updating data...";
        if (isLoading) return "Loading data...";
        if (isDepListLoading) return "Fetching dependency list...";
        return "";
    };
    const isUIBusy = isUpdateLoading || isLoading || isDepListLoading;

    return (
        <Modal className="roles-and-perm" isOpen={isOpen} toggle={toggleModal} backdrop="static" size="xl">
            <Form>
                <ActionModalHeader
                    title="Edit Role Permissions"
                    onCancel={handleCancel}
                    cancelButtonDisabled={isUIBusy}
                    actionButton={
                        <Button disabled={isUIBusy} onClick={hadleSaveChanges} variant="submit" size="medium">Save Changes</Button>
                    }
                />
                <ModalBody className="pt-1 pl-5 pr-5 pb-4">
                    {isUIBusy && <Spinner text={getUIBusyMessage()} role="status" /> }
                    {!isUIBusy && <div>
                        <FormGroup className="mb-1 mt-3">
                            <Row className="align-items-center">
                                <Col md="2">
                                    <Label for="roleName" className="mb-0">Role Name</Label>
                                </Col>
                                <Col md="3" className="pl-0">
                                    {roleNameTextBox}
                                </Col>
                            </Row>
                        </FormGroup>


                        <Label className="mt-2">Systems (Configure permissions for each applicable system)</Label>
                        <Nav tabs className="mt-2">
                            {systemTabs.map((tab) => (
                                <NavItem key={tab}>
                                    <NavLink
                                        className={classnames(
                                            'custom-tab',
                                            { 'active-tab': activeTab === tab }
                                        )}
                                        onClick={() => toggleTab(tab)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {tab}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        <TabContent activeTab={activeTab} className="mt-4">
                            {systemTabs.map((tab) => (
                                <TabPane tabId={tab} key={tab} role="tabpanel">
                                    <PermissionsTabContent
                                        key={`tab-${tab}`}
                                        tab={tab}
                                        activeTab={activeTab}
                                        localRolePermissionsDetail={localRolePermissionsDetail}
                                        system={system}
                                        permissionLevels={permissionLevels}
                                        isAppUserRole={isAppUserRole}
                                        handleAllCheckboxesToggle={handleAllCheckboxesToggle}
                                        renderPermissionCheckboxes={renderPermissionCheckboxes}
                                        isPermissionsListEmpty={isPermissionsListEmpty}
                                        isParentChecked={isParentChecked}
                                    />
                                </TabPane>
                            ))}
                        </TabContent>
                    </div>}
                </ModalBody>
            </Form>
        </Modal>
    );
}

export default EditRolePermissionsModal;
